// src/components/home/StoryEditor.jsx
import React, {
  useState, useRef, useEffect, useMemo, useCallback,
} from 'react';
import { db, storage } from '../../firebase';
import {
  doc, updateDoc, deleteDoc, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext';
import {
  validateMedia,
  uploadMedia,
  isVideoFile,
  MAX_STORY_PHOTOS,
} from '../../utils/feedHelpers';

/* ================================================================== */
/* Storage helpers                                                     */
/* ================================================================== */

const storagePathFromUrl = (url) => {
  if (!url) return null;
  const m = url.match(/\/o\/([^?]+)/);
  return m ? decodeURIComponent(m[1]) : null;
};

const safeDeleteFromStorage = async (url) => {
  const path = storagePathFromUrl(url);
  if (!path) return;
  try {
    await deleteObject(ref(storage, path));
  } catch (err) {
    if (err?.code !== 'storage/object-not-found') {
      console.warn('Storage delete skipped:', path, err?.code || err);
    }
  }
};

/* ================================================================== */
/* Media Tile                                                          */
/* ================================================================== */

const MediaTile = ({ item, onRemove, busy, danger = false }) => (
  <div className="relative group rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
    {item.type === 'video' ? (
      <video
        src={item.url}
        className="w-full h-24 object-cover"
        muted
        playsInline
        preload="metadata"
      />
    ) : (
      <img
        src={item.url}
        alt=""
        className="w-full h-24 object-cover"
        loading="lazy"
      />
    )}

    {item.type === 'video' && (
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-8 h-8 rounded-full bg-black/60 flex items-center justify-center backdrop-blur-sm">
          <i className="fas fa-play text-white text-xs" aria-hidden="true" />
        </div>
      </div>
    )}

    <button
      type="button"
      onClick={onRemove}
      disabled={busy}
      aria-label="Remove"
      className={`absolute top-1.5 right-1.5 w-7 h-7 rounded-full flex items-center justify-center transition disabled:opacity-50 shadow-md z-10 active:scale-90 ${
        danger
          ? 'bg-red-500 text-white opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100'
          : 'bg-black/70 text-white opacity-100 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100'
      }`}
    >
      <i className="fas fa-times text-xs" aria-hidden="true" />
    </button>
  </div>
);

/* ================================================================== */
/* Story Editor                                                        */
/* ================================================================== */

const StoryEditor = ({ story, onClose, onUpdated, onDeleted }) => {
  const { user, isAdmin } = useAuth();

  const [caption, setCaption] = useState(story.caption || '');
  const [items, setItems] = useState(() =>
    Array.isArray(story.items) && story.items.length
      ? story.items.map((i) => ({ url: i.url, type: i.type || 'image' }))
      : (story.photos || []).map((url) => ({ url, type: 'image' })),
  );
  const [newFiles, setNewFiles] = useState([]);
  const [newPreviews, setNewPreviews] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const previewUrlsRef = useRef([]);
  const removedExistingItemsRef = useRef([]);
  const busyRef = useRef(false);
  const captionRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrlsRef.current = [];
    };
  }, []);

  useEffect(() => {
    setConfirmDelete(false);
  }, [story.id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !busyRef.current) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (items.length === 0 && newFiles.length === 0) {
      const t = setTimeout(() => captionRef.current?.focus(), 120);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [items.length, newFiles.length]);

  useEffect(() => {
    if (confirmDelete && (caption !== (story.caption || '') || newFiles.length > 0)) {
      setConfirmDelete(false);
    }
  }, [caption, newFiles.length, confirmDelete, story.caption]);

  const totalItems = items.length + newFiles.length;
  const canAddMore = totalItems < MAX_STORY_PHOTOS;
  const removedCount = removedExistingItemsRef.current.length;
  const slotsLeft = MAX_STORY_PHOTOS - totalItems;
  const slotsUsedPct = Math.min(100, (totalItems / MAX_STORY_PHOTOS) * 100);

  const handleFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;

    const slots = MAX_STORY_PHOTOS - totalItems;
    if (slots <= 0) return;

    const errors = [];
    const accepted = [];
    for (const f of picked) {
      if (accepted.length >= slots) break;
      const err = validateMedia(f);
      if (err) errors.push(err);
      else accepted.push(f);
    }
    setError(errors.length ? errors.join('\n') : '');
    if (!accepted.length) return;

    const nextFiles = [...newFiles, ...accepted];

    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    const nextPreviews = nextFiles.map((f) => ({
      url: URL.createObjectURL(f),
      type: isVideoFile(f) ? 'video' : 'image',
    }));
    previewUrlsRef.current = nextPreviews.map((p) => p.url);

    setNewFiles(nextFiles);
    setNewPreviews(nextPreviews);
  };

  const removeNewFile = (idx) => {
    const url = newPreviews[idx]?.url;
    if (url) URL.revokeObjectURL(url);
    previewUrlsRef.current = previewUrlsRef.current.filter((u) => u !== url);

    setNewFiles(newFiles.filter((_, i) => i !== idx));
    setNewPreviews(newPreviews.filter((_, i) => i !== idx));
  };

  const removeExistingItem = (idx) => {
    const removed = items[idx];
    if (removed) removedExistingItemsRef.current.push(removed);
    setItems(items.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (busyRef.current) return;

    if (!caption.trim() && items.length === 0 && newFiles.length === 0) {
      setError('Story must have a caption or at least one media item.');
      return;
    }

    busyRef.current = true;
    setBusy(true);
    setError('');
    setProgress({ done: 0, total: newFiles.length });

    try {
      const uploaded = [];
      for (let i = 0; i < newFiles.length; i++) {
        const file = newFiles[i];
        const url = await uploadMedia(file, 'stories');
        uploaded.push({
          url,
          type: isVideoFile(file) ? 'video' : 'image',
        });
        setProgress({ done: i + 1, total: newFiles.length });
      }

      const finalItems = [...items, ...uploaded];

      const updates = {
        caption: caption.trim(),
        items: finalItems,
        photos: finalItems.map((i) => i.url),
        updatedAt: serverTimestamp(),
      };

      const wasApproved = story.approved === true;
      const needsReapproval = !isAdmin && wasApproved;
      if (needsReapproval) {
        updates.approved = false;
        updates.approvalStatus = 'pending';
        updates.approvedBy = null;
        updates.approvedAt = null;
      }

      await updateDoc(doc(db, 'stories', story.id), updates);

      const urlsToDelete = new Set(
        removedExistingItemsRef.current.map((r) => r?.url).filter(Boolean),
      );
      for (const url of urlsToDelete) {
        try {
          await safeDeleteFromStorage(url);
        } catch (err) {
          console.warn('Could not delete old item', err);
        }
      }

      onUpdated?.({
        ...story,
        ...updates,
        updatedAt: Timestamp.now(),
      });
      onClose();
    } catch (err) {
      console.error('Story update failed:', err);
      setError(err.message || 'Failed to update story.');
    } finally {
      setBusy(false);
      busyRef.current = false;
    }
  };

  const handleDelete = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const urlsToDelete = new Set(
        [
          ...(story.items || []).map((i) => i?.url),
          ...(story.photos || []),
        ].filter(Boolean),
      );

      for (const url of urlsToDelete) {
        await safeDeleteFromStorage(url);
      }

      await deleteDoc(doc(db, 'stories', story.id));
      onDeleted?.(story.id);
      onClose();
    } catch (err) {
      console.error('Story delete failed:', err);
      setError(err.message || 'Failed to delete story.');
    } finally {
      setBusy(false);
      busyRef.current = false;
    }
  };

  return (
    <div
      className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 overflow-y-auto"
      onClick={() => !busy && onClose?.()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="story-editor-title"
      style={{
        paddingTop: 'calc(0.75rem + env(safe-area-inset-top))',
        paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))',
      }}
    >
      <div
        className="bg-white dark:bg-gray-800 w-full rounded-2xl max-w-lg max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center px-4 sm:px-5 py-3 border-b dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <i className="fas fa-pen text-orange" aria-hidden="true" />
            <h3
              id="story-editor-title"
              className="text-base sm:text-lg font-bold text-gray-800 dark:text-white truncate"
            >
              Edit Story
            </h3>
            <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0">
              {totalItems}/{MAX_STORY_PHOTOS}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="w-9 h-9 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-50 flex items-center justify-center transition flex-shrink-0 active:scale-90"
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>

        <div className="p-4 sm:p-5 overflow-y-auto flex-1 space-y-4 min-h-0">
          {totalItems > 0 && (
            <div className="flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400">
              <div className="flex-1 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-orange transition-all duration-300"
                  style={{ width: `${slotsUsedPct}%` }}
                />
              </div>
              <span className="font-medium whitespace-nowrap">
                {slotsLeft} slot{slotsLeft === 1 ? '' : 's'} left
              </span>
            </div>
          )}

          {items.length > 0 && (
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                Current media ({items.length}
                {removedCount > 0 && (
                  <span className="text-red-500 ml-1">
                    · {removedCount} marked for deletion
                  </span>
                )}
                )
              </label>
              <div className="grid grid-cols-3 gap-2">
                {items.map((item, i) => (
                  <MediaTile
                    key={`${item.url}-${i}`}
                    item={item}
                    onRemove={() => removeExistingItem(i)}
                    busy={busy}
                    danger
                  />
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Add more photos / videos
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy || !canAddMore}
                className="flex-1 flex items-center justify-center gap-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50 transition active:scale-95"
              >
                <i className="fas fa-folder-open" aria-hidden="true" />
                Gallery
              </button>
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                disabled={busy || !canAddMore}
                className="flex-1 flex items-center justify-center gap-2 bg-orange hover:bg-orange-dark text-white py-2.5 rounded-lg text-sm font-medium disabled:opacity-50 transition active:scale-95"
              >
                <i className="fas fa-camera" aria-hidden="true" />
                Camera
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/mp4,video/webm,video/quicktime"
              multiple
              onChange={handleFiles}
              disabled={busy || !canAddMore}
              className="hidden"
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFiles}
              disabled={busy || !canAddMore}
              className="hidden"
            />
            <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1.5">
              {canAddMore
                ? `${slotsLeft} slot(s) remaining · max ${MAX_STORY_PHOTOS}`
                : `Maximum of ${MAX_STORY_PHOTOS} media items reached.`}
            </p>
          </div>

          {newPreviews.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                New media ({newPreviews.length})
              </p>
              <div className="grid grid-cols-3 gap-2">
                {newPreviews.map((p, i) => (
                  <MediaTile
                    key={`${p.url}-${i}`}
                    item={p}
                    onRemove={() => removeNewFile(i)}
                    busy={busy}
                  />
                ))}
              </div>
            </div>
          )}

          {items.length === 0 && newFiles.length === 0 && (
            <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-xs bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-dashed dark:border-gray-700">
              <i
                className="fas fa-image text-2xl mb-2 block opacity-40"
                aria-hidden="true"
              />
              No media left in this story. Add at least one photo/video, or
              save with just a caption.
            </div>
          )}

          <div>
            <label
              htmlFor="story-caption"
              className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300"
            >
              Caption
            </label>
            <textarea
              id="story-caption"
              ref={captionRef}
              rows={2}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              disabled={busy}
              maxLength={200}
              placeholder="Say something about this story…"
              className="w-full p-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange resize-none"
            />
            <p className="text-[10px] text-gray-400 text-right mt-0.5">
              {caption.length}/200
            </p>
          </div>

          {!isAdmin && story.approved === true && (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg text-xs text-yellow-800 dark:text-yellow-200 flex items-start gap-2">
              <i className="fas fa-info-circle mt-0.5" aria-hidden="true" />
              <div>
                <strong>Your changes will require re-approval.</strong> Since
                you're not an admin, saving will put the story back into the
                pending queue.
              </div>
            </div>
          )}

          {busy && progress.total > 0 && (
            <div className="text-sm text-gray-600 dark:text-gray-300">
              <div
                className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden"
                role="progressbar"
                aria-valuenow={progress.done}
                aria-valuemin={0}
                aria-valuemax={progress.total}
              >
                <div
                  className="h-full bg-orange transition-all duration-300"
                  style={{ width: `${(progress.done / progress.total) * 100}%` }}
                />
              </div>
              <p className="text-xs mt-1 text-center">
                Uploading {progress.done} of {progress.total}…
              </p>
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 text-xs rounded-md p-2 whitespace-pre-line"
            >
              {error}
            </div>
          )}
        </div>

        <div
          className="px-4 sm:px-5 py-3 border-t dark:border-gray-700 flex justify-between items-center gap-2 flex-shrink-0 bg-white dark:bg-gray-800"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
        >
          {!confirmDelete ? (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              disabled={busy}
              className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium flex items-center gap-2 disabled:opacity-50 transition active:scale-95"
            >
              <i className="fas fa-trash-alt" aria-hidden="true" />
              <span className="hidden sm:inline">Delete</span>
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xs text-red-600 dark:text-red-400 font-medium">
                Sure?
              </span>
              <button
                type="button"
                onClick={handleDelete}
                disabled={busy}
                className="bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1.5 rounded font-medium disabled:opacity-50 active:scale-95"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                disabled={busy}
                className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm disabled:opacity-50 transition active:scale-95"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy || (!caption.trim() && totalItems === 0)}
              className="px-4 py-2 bg-orange hover:bg-orange-dark text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2 transition active:scale-95"
            >
              {busy ? (
                <>
                  <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                  Saving…
                </>
              ) : isAdmin ? (
                <>
                  <i className="fas fa-save" aria-hidden="true" />
                  Save
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane" aria-hidden="true" />
                  Save &amp; Resubmit
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryEditor;